const API_BASE = import.meta.env.VITE_API_URL || '/api';

/**
 * Resolve image URL - converts relative paths to full URLs using API base
 * iOS app prepends its baseURL, but dashboard needs full URL
 */
export function resolveImageUrl(path?: string): string | undefined {
  if (!path) return undefined;
  if (path.startsWith('http')) return path;
  // Some paths already have /api prefix (submissions), some don't (pins)
  if (path.startsWith('/api/')) {
    // Extract just the origin from API_BASE and prepend it
    const origin = API_BASE.replace(/\/api$/, '');
    return `${origin}${path}`;
  }
  // Path like /pins/... needs /api prefix
  return `${API_BASE}${path}`;
}

// Validate login credentials against the current environment
// Uses an authenticated endpoint (/admin/summary) so invalid creds get 401
// Does NOT use fetchApi to avoid the automatic 401 reload behavior
export async function validateLogin(username: string, password: string): Promise<boolean> {
  const response = await fetch(`${API_BASE}/admin/summary`, {
    headers: {
      Authorization: `Basic ${btoa(`${username}:${password}`)}`,
    },
  });
  return response.ok;
}

interface ApiOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
}

async function fetchApi<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;

  // Get auth from localStorage
  const auth = localStorage.getItem('admin_auth');

  const response = await fetch(`${API_BASE}${endpoint}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(auth ? { Authorization: `Basic ${auth}` } : {}),
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (response.status === 401) {
    // Clear auth and redirect to login
    localStorage.removeItem('admin_auth');
    window.location.reload();
    throw new Error('Unauthorized');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'API request failed');
  }

  return data;
}

// Auth
export function setAuth(username: string, password: string): void {
  const auth = btoa(`${username}:${password}`);
  localStorage.setItem('admin_auth', auth);
}

export function clearAuth(): void {
  localStorage.removeItem('admin_auth');
}

export function isAuthenticated(): boolean {
  return !!localStorage.getItem('admin_auth');
}

// Health
export async function getHealth() {
  return fetchApi<{ status: string; timestamp: string; services: Record<string, string> }>('/health');
}

// Pins
// When `status` is undefined, no status param is sent. The backend then falls back
// to the `moderation_sandboxing_enabled` global setting (approved-only when ON,
// approved + ai_approved when OFF). Pass an explicit status (e.g. 'all', 'pending',
// 'approved') to override and see exactly that bucket.
export async function getPins(page = 1, limit = 20, status?: string) {
  const statusParam = status ? `&status=${encodeURIComponent(status)}` : '';
  return fetchApi<{ success: boolean; data: { pins: unknown[]; pagination: { total: number; page: number; limit: number; pages: number } } }>(
    `/pins/search?page=${page}&limit=${limit}${statusParam}`
  );
}

export async function searchPins(query: string, page = 1, limit = 20, status?: string) {
  const statusParam = status ? `&status=${encodeURIComponent(status)}` : '';
  return fetchApi<{ success: boolean; data: { pins: unknown[]; pagination: { total: number; page: number; limit: number; pages: number } } }>(
    `/pins/search?q=${encodeURIComponent(query)}&page=${page}&limit=${limit}${statusParam}`
  );
}

export async function createPin(data: Record<string, unknown>) {
  return fetchApi<{ success: boolean; data: { pin: unknown } }>('/pins', { method: 'POST', body: data });
}

export async function updatePin(id: string, data: Record<string, unknown>) {
  return fetchApi(`/pins/${id}`, { method: 'PUT', body: data });
}

// Sites
export async function getSites() {
  return fetchApi<{ success: boolean; data: { sites: unknown[] } }>('/sites');
}

export async function createSite(data: Record<string, unknown>) {
  return fetchApi<{ status: string; data: { site: unknown } }>('/sites', { method: 'POST', body: data });
}

// Stores
export async function getStores(siteId?: string, page = 1, limit = 50) {
  let query = `?page=${page}&limit=${limit}`;
  if (siteId) query += `&siteId=${siteId}`;
  return fetchApi<{ success: boolean; data: { stores: unknown[]; pagination?: { total: number; page: number; limit: number; pages: number } } }>(`/stores${query}`);
}

export async function createStore(data: Record<string, unknown>) {
  return fetchApi<{ status: string; data: { store: unknown } }>('/stores', { method: 'POST', body: data });
}

// Submissions
export async function getSubmissions(page = 1, limit = 20, status?: string, search?: string) {
  let query = `?page=${page}&limit=${limit}`;
  if (status) query += `&status=${encodeURIComponent(status)}`;
  if (search) query += `&q=${encodeURIComponent(search)}`;
  return fetchApi<{ success: boolean; data: { submissions: unknown[]; pagination: { total: number } } }>(
    `/submissions${query}`
  );
}

export async function updateSubmission(id: string, data: { status: string }) {
  return fetchApi(`/submissions/${id}`, { method: 'PATCH', body: data });
}

// Stats
export async function getGlobalStats() {
  return fetchApi<{
    success: boolean;
    data: {
      totalUsers: number;
      totalEvents: number;
      eventsToday: number;
      eventsThisWeek: number;
      eventsByType: Record<string, number>;
    };
  }>('/stats/global');
}

// Feedback (admin - get all feedback)
export async function getFeedback(page = 1, limit = 20, status?: string, type?: string) {
  let query = `?page=${page}&limit=${limit}`;
  if (status && status !== 'all') query += `&status=${encodeURIComponent(status)}`;
  if (type && type !== 'all') query += `&type=${encodeURIComponent(type)}`;
  return fetchApi<{
    success: boolean;
    data: {
      feedback: Array<{
        id: string;
        user_id?: string;
        anonymous_id?: string;
        type: string;
        message: string;
        pin_sku?: string;
        status: string;
        created_at: string;
        updated_at: string;
      }>;
      pagination: { total: number; page: number; limit: number; totalPages: number };
    };
  }>(`/feedback/admin${query}`);
}

export async function updateFeedbackStatus(id: string, status: string) {
  return fetchApi(`/feedback/admin/${id}`, { method: 'PATCH', body: { status } });
}

// Availability Events (admin)
export async function getAvailabilityEvents(page = 1, limit = 20, eventType?: string) {
  let query = `?limit=${limit}&offset=${(page - 1) * limit}`;
  if (eventType) query += `&eventType=${encodeURIComponent(eventType)}`;
  return fetchApi<{
    success: boolean;
    data: {
      events: Array<{
        id: string;
        pinSku: string;
        pinName?: string;
        storeId: string;
        storeName?: string;
        siteName?: string;
        eventType: string;
        scanMethod?: string;
        quantity?: number;
        notes?: string;
        createdAt: string;
      }>;
      pagination: { total: number; limit: number; offset: number; hasMore: boolean };
    };
  }>(`/availability/admin/events${query}`);
}

export async function getAvailabilityStats() {
  return fetchApi<{
    success: boolean;
    data: {
      totalEvents: number;
      eventsToday: number;
      eventsThisWeek: number;
      eventsByType: Record<string, number>;
    };
  }>('/availability/admin/stats');
}

// Users (admin)
export async function getUserStats() {
  return fetchApi<{
    success: boolean;
    data: {
      totalUsers: number;
      authenticatedUsers: number;
      anonymousUsers: number;
      activeToday: number;
      activeThisWeek: number;
    };
  }>('/users/admin/stats');
}

// Settings
export async function getSettings() {
  return fetchApi<{
    success: boolean;
    data: {
      moderationEnabled: boolean;
      aiApprovedPinsRequireModeration: boolean;
      submissionLimitsEnabled: boolean;
      dailySubmissionLimit: number;
      statsTrackingEnabled: boolean;
      leaderboardsEnabled: boolean;
      achievementsEnabled: boolean;
    };
  }>('/settings');
}

export async function updateSettings(settings: Record<string, unknown>) {
  return fetchApi('/settings', { method: 'PUT', body: settings });
}

// Dashboard Summary
export async function getDashboardSummary() {
  return fetchApi<{
    success: boolean;
    data: {
      pins: { total: number; pending: number; approved: number; aiApproved: number; rejected: number };
      submissions: { total: number; pending: number };
      users: { total: number; authenticated: number; anonymous: number; activeToday: number };
      events: { today: number; thisWeek: number; total: number };
      feedback: { total: number; pending: number };
    };
  }>('/admin/summary');
}

// Recent Activity
export async function getRecentActivity(limit = 20) {
  return fetchApi<{
    success: boolean;
    data: {
      activity: Array<{
        id: string;
        type: string;
        title: string;
        meta: string;
        icon: string;
        iconBg: string;
        createdAt: string;
      }>;
    };
  }>(`/admin/activity?limit=${limit}`);
}

// System Health
export async function getSystemHealth() {
  return fetchApi<{
    success: boolean;
    data: {
      status: string;
      timestamp: string;
      services: Record<string, { status: string; latency?: string; value?: string }>;
      responseTime: number;
    };
  }>('/admin/health');
}

// Storage Usage
export async function getStorageUsage() {
  return fetchApi<{
    success: boolean;
    data: {
      storage: {
        r2Images: { used: string; total: string; percent: number };
        d1Database: { used: string; total: string; percent: number };
        kvStorage: { used: string; total: string; percent: number };
      };
      counts: { pins: number; images: number; events: number };
    };
  }>('/admin/storage');
}

// Request Stats (7-day chart)
export async function getRequestStats() {
  return fetchApi<{
    success: boolean;
    data: {
      days: string[];
      counts: number[];
      percentages: number[];
    };
  }>('/admin/request-stats');
}
